
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { FaEye, FaCheck, FaMoneyBillWave, FaExchangeAlt } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PageHeading from "../PageHeading";
import { format } from "date-fns";
import ViewPurchaseModal from "../ViewPurchaseModal";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import TableSkeleton from "../TableSkeleton";
import ConfirmActionModal from "../ConfirmDeleteModal";
import PurchaseMedicineModal from "./PurchaseMedicineModal";
import DynamicSelect from "../DynamicSelect";
import ExportOptions from "../ExportOptions";
import SmartDropdown from "../Shared/SmartDropdown";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FiEdit } from "react-icons/fi";
import PurchaseMedicineAddPaymentModal from "./PurchaseMedicineAddPaymentModal";
import Pagination from "../Shared/Pagination";

const API_BASE =
  process.env.REACT_APP_API_BASE_PURCHASE ||
  "https://api.muktihospital.com/api/medicine-purchase";
const SUPPLIER_API = "https://api.muktihospital.com/api/medicine-supplier";
const API_KEY =
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const PurchaseMedicine = () => {
  const [purchases, setPurchases] = useState([]);
  const [filteredPurchases, setFilteredPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editPurchaseId, setEditPurchaseId] = useState(null);
  const [viewPurchase, setViewPurchase] = useState(null);
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownTriggerRefs = useRef({});
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentPurchaseData, setPaymentPurchaseData] = useState(null);
  const itemsPerPage = 10;

  const handleAddMoney = (purchase) => {
    setPaymentPurchaseData(purchase);
    setPaymentModalOpen(true);
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(SUPPLIER_API, {
        headers: { "X-Api-Key": API_KEY },
      });
      const supplierArray = Array.isArray(response.data.suppliers)
        ? response.data.suppliers
        : [];
      setSuppliers(supplierArray);
    } catch (error) {
      console.error("Fetch suppliers error:", error);
      toast.error("Failed to fetch suppliers");
    }
  };

const fetchPurchases = async () => {
    setIsLoading(true);
    try {
      let allPurchases = [];
      let page = 1;
      let totalPages = 1;
      let totalItems = 0;

      // Fetch all pages
      while (page <= totalPages) {
        const response = await axios.get(
          `${API_BASE}?page=${page}&limit=10000`,
          {
            headers: { "X-Api-Key": API_KEY },
          }
        );
        const purchaseArray = Array.isArray(response.data.purchases)
          ? response.data.purchases
          : [];
        allPurchases = [...allPurchases, ...purchaseArray];
        totalPages = response.data.pagination?.totalPages || 1;
        totalItems = response.data.pagination?.totalItems || allPurchases.length;
        page++;
      }

      const purchasesProcessed = allPurchases.map((purchase) => {
        let parsedItems = [];
        try {
            parsedItems = typeof purchase.items === 'string' 
                ? JSON.parse(purchase.items) 
                : (purchase.items || []);
        } catch (error) {
            console.error("Error parsing items:", error);
            parsedItems = [];
        }

        return {
            ...purchase,
            supplierName: purchase.supplier?.supplierName || "Unknown",
            items: parsedItems,
        };
      });

      setPurchases(purchasesProcessed);
      setFilteredPurchases(purchasesProcessed);
      setTotalRecords(totalItems);
    } catch (error) {
      console.error("Fetch purchases error:", error);
      toast.error(error.response?.data?.error || "Failed to fetch purchases");
      setPurchases([]);
      setFilteredPurchases([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
  }, []);

  useEffect(() => {
    fetchPurchases();
  }, []);

  const handleDelete = async (purchaseIds) => {
    setIsLoading(true);
    try {
      await Promise.all(
        purchaseIds.map((id) =>
          axios.delete(`${API_BASE}/${id}`, {
            headers: { "X-Api-Key": API_KEY },
          })
        )
      );
      toast.success(`${purchaseIds.length} purchase record(s) deleted successfully`);
      setSelectedRows([]);
      fetchPurchases();
    } catch (error) {
      console.error("Error deleting purchase records:", error);
      toast.error(
        error.response?.data?.error || "Failed to delete purchase record(s)"
      );
    } finally {
      setIsLoading(false);
      setDeleteModalOpen(false);
    }
  };

  const handleView = (purchase) => {
    setViewPurchase(purchase);
    setViewModalOpen(true);
  };

  const handleEdit = (purchase) => {
    setEditPurchaseId(purchase.id);
    setPurchaseModalOpen(true);
  };

  const handleSingleDelete = (purchaseId) => {
    setSelectedRows([purchaseId]);
    setDeleteModalOpen(true);
  };

  const handleSelectAll = (e) => {
    const currentPageData = filteredPurchases.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );
    if (e.target.checked) {
      const newSelected = currentPageData.map((p) => p.id);
      setSelectedRows(newSelected);
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSupplierChange = (selectedOption) => {
    setSelectedSupplier(selectedOption);
    setCurrentPage(1);
    if (selectedOption && selectedOption.value !== null) {
      const filtered = purchases.filter(
        (p) => String(p.supplierId) === String(selectedOption.value)
      );
      setFilteredPurchases(filtered);
    } else {
      setFilteredPurchases(purchases);
    }
  };

const handleStatusChange = async (purchase) => {
  const newStatus = purchase.status === "Pending" ? "Complete" : "Pending";

  setIsLoading(true);
  try {
    await axios.patch(
      `${API_BASE}/${purchase.id}/status`, 
      { status: newStatus }, 
      { headers: { "X-Api-Key": API_KEY } }
    );
    
    toast.success(`Status updated to ${newStatus}`);
    await fetchPurchases();
  } catch (err) {
    console.error("Error updating status:", err);
    toast.error(err.response?.data?.error || "Failed to update status.");
  } finally {
    setIsLoading(false);
  }
};

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const supplierOptions = [
    { value: null, label: "All Suppliers" },
    ...suppliers.map((supplier) => ({
      value: supplier.id,
      label: supplier.supplierName,
    })),
  ];

  const totalPages = Math.ceil(filteredPurchases.length / itemsPerPage) || 1;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = filteredPurchases.slice(indexOfFirstItem, indexOfLastItem);

  const skeletonColumns = [
    { width: 16, height: 16, className: "p-3", skeletonClassName: "ml-3" },
    { width: 80, height: 14, className: "p-3" },
    { width: 80, height: 14, className: "p-3" },
    { width: 80, height: 14, className: "p-3" },
    { width: 80, height: 14, className: "p-3" },
    { width: 80, height: 14, className: "p-3" },
    { width: 80, height: 14, className: "p-3" },
    { width: 80, height: 14, className: "p-3" },
    { width: 80, height: 14, className: "p-3" },
    { width: 80, height: 14, className: "p-3" },
    { width: 80, height: 14, className: "p-3" },
    { width: 80, height: 14, className: "p-3" },
    {
      width: 120,
      height: 28,
      className: "p-3 flex gap-2",
      skeletonClassName: "inline-block",
    },
  ];

  const toggleExportDropdown = () => setExportDropdownOpen((prev) => !prev);

  const breadcrumbs = [
      { label: "Dashboard", url: "/" },
      { label: "Pharmacy"},
      { label: "Medicine Purchase List" },
  ];

  const headers = [
      "Sl",
      "Bill No",
      "Date",
      "Supplier",
      "Box Qty",
      "Quantity",
      "Net Amount",
      "Due Amount",
      "Status",
      "Authorized By",
      "Actions",
  ];

    const dataMapper = (() => {
      let counter = 0;
      return (item) => {
        counter += 1;
        return [
          counter,
          item?.billNo || '-',
          item?.purchaseDate
          ? new Date(item.purchaseDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
          : '-',
          item?.supplierName || '-',
          item?.items?.reduce((sum, i) => sum + (i.boxQty || 0), 0).toFixed(2) || '0.00',
          item?.items?.reduce((sum, i) => sum + (i.quantity || 0), 0) || '0',
          item?.netAmount?.toFixed(2) || '0.00',
          item?.userName || '-'
        ].join("\t");
      };
    })();

  return (
    <div>
      <ToastContainer position="top-right" />
      <PageHeading
        title="Medicine Purchase List"
        breadcrumbs={breadcrumbs}
      />
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Purchase List
            </h2>
            {selectedRows.length > 0 && (
              <button
                onClick={() => setDeleteModalOpen(true)}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                disabled={isLoading}
                title="Delete selected rows"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex items-start sm:items-center flex-col sm:flex-row gap-3">
            {isLoading ? (
              <Skeleton height={40} width={200} />
            ) : (
              <div className="w-full h-10">
                <DynamicSelect
                  options={supplierOptions}
                  onChange={handleSupplierChange}
                  value={selectedSupplier}
                  isSearchable={true}
                  placeholder="Select Supplier..."
                />
              </div>
            )}
          <div className="flex items-center justify-between w-full">
                {isLoading ? (
              <Skeleton height={40} width={150} />
            ) : (
              <button
                onClick={() => {
                  setEditPurchaseId(null);
                  setPurchaseModalOpen(true);
                }}
                className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0 w-full sm:w-auto"
                disabled={isLoading}
              >
                + Purchase Medicine
              </button>
            )}
            <ExportOptions
                data={filteredPurchases}
                dropdownOpen={exportDropdownOpen}
                toggleDropdown={toggleExportDropdown}
                title="Medicine Purchase List"
                headers={headers}
                dataMapper={dataMapper}
              />
          </div>
          </div>
        </div>

        <PurchaseMedicineModal
          isOpen={purchaseModalOpen}
          purchaseId={editPurchaseId}
          onClose={() => {
            setPurchaseModalOpen(false);
            setEditPurchaseId(null);
          }}
          onSave={() => {
            setPurchaseModalOpen(false);
            setEditPurchaseId(null);
            fetchPurchases();
          }}
        />

        <ViewPurchaseModal
          isOpen={viewModalOpen}
          onDelete={(id) => handleDelete([id])}
          onClose={() => setViewModalOpen(false)}
          purchase={viewPurchase}
        />

        <ConfirmActionModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={() => handleDelete(selectedRows)}
          ids={selectedRows}
          entityName="purchase"
          title="Confirm Delete"
          message={
            selectedRows.length > 1
              ? `Delete ${selectedRows.length} purchase(s)?`
              : `Delete this purchase?`
          }
          confirmText="Delete"
          cancelText="Cancel"
        />

        <div className="overflow-x-auto print-wrapper">
          <table className="min-w-[1440px] w-full divide-y divide-gray-200 print-table">
            <thead>
              <tr className="bg-gray-100">
                <th className="text-left print-hidden">
                  <div className="relative left-3 -top-[2px]">
                    <input
                      type="checkbox"
                      id="agreement"
                      className="hidden peer print-hidden"
                      onChange={handleSelectAll}
                      checked={
                        currentData.length > 0 &&
                        currentData.every((item) =>
                          selectedRows.includes(item.id)
                        )
                      }
                      disabled={isLoading}
                    />
                    <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                      <FaCheck className="text-slate-100" />
                    </span>
                    <label
                      htmlFor="agreement"
                      className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                    ></label>
                  </div>
                </th>
                {[
                  "Sl",
                  "Purchase ID",
                  "Bill No",
                  "Date & Time",
                  "Supplier",
                  "Box Qty",
                  "Quantity",
                  "Net Amount",
                  "Due Amount",
                  "Status",
                  "Authorized By",
                  "Actions",
                ].map((h, idx) => (
                  <th
                    key={idx}
                    className="p-3 text-left text-sm font-medium text-gray-700 uppercase"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <TableSkeleton columns={skeletonColumns} rowCount={10} />
              ) : (
                currentData.map((p, index) => (
                  <tr key={p.id} className="border-b hover:bg-gray-50">
                    {console.log(p)
                    }
                    <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color print-hidden">
                      <div className="relative left-3 -top-[3px]">
                        <input
                          type="checkbox"
                          id={`purchase-${index}`}
                          className="hidden peer print-hidden"
                          checked={selectedRows.includes(p.id)}
                          onChange={() => handleRowSelect(p.id)}
                          disabled={isLoading}
                        />
                        <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                          <FaCheck className="text-slate-100" />
                        </span>
                        <label
                          htmlFor={`purchase-${index}`}
                          className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                        ></label>
                      </div>
                    </td>
                    <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3" role="cell">
                        {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                    <td className="p-3 text-sm text-gray-700">
                      {p.id || "-"}
                    </td>
                    <td className="p-3 text-sm text-gray-700">
                      {p.billNo || "-"}
                    </td>
                    <td className="p-3 text-sm text-gray-700">
                      {p.purchaseDate
                        ? format(new Date(p.purchaseDate), "MM/dd/yyyy")
                        : "-"}
                    </td>
                    <td className="p-3 text-sm text-gray-700">
                      {p.supplierName || "-"}
                    </td>
                    <td className="p-3 text-sm text-gray-700">
                    {(Array.isArray(p.items) ? p.items : [])
                      .reduce((sum, item) => sum + (item.boxQty || 0), 0)
                      .toFixed(2)}
                  </td>
                  <td className="p-3 text-sm text-gray-700">
                    {(Array.isArray(p.items) ? p.items : [])
                      .reduce((sum, item) => sum + (item.quantity || 0), 0)}
                  </td>
                    <td className="p-3 text-sm text-gray-700">
                      {p.netAmount ? p.netAmount.toFixed(2) : "0.00"}
                    </td>
                    {/* Due Amount Column */}
                    <td className="p-3 text-sm text-gray-700 font-medium">
                      {p.dueAmount ? p.dueAmount.toFixed(2) : "0.00"}
                    </td>
                    {/* Status Column */}
                    <td className="p-3 text-sm">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        p.status === "Complete" 
                          ? "bg-green-100 text-green-700" 
                          : "bg-yellow-100 text-yellow-700"
                      }`}>
                        {p.status || "Pending"}
                      </span>
                    </td>
                    <td className="p-3 text-sm text-gray-700">
                      {p.authorizedBy || "Unknown"}
                    </td>
                    <td className="p-3 text-right print-hidden-in-print">
                      {/* Trigger Button */}
                      <button
                        ref={(el) => (dropdownTriggerRefs.current[p.id] = el)}
                        onClick={() => setActiveDropdown(activeDropdown === p.id ? null : p.id)}
                        className="size-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors text-gray-500"
                      >
                        <BsThreeDotsVertical size={14} />
                      </button>

                      {/* Smart Dropdown */}
                      <SmartDropdown
                        isOpen={activeDropdown === p.id}
                        triggerRef={{ current: dropdownTriggerRefs.current[p.id] }}
                        onClose={() => setActiveDropdown(null)}
                        options={{ width: 160, align: "right" }}
                      >
                        <div className="py-1">
                          {/* Add this button */}
                          <button
                              onClick={() => {
                                handleAddMoney(p);
                                setActiveDropdown(null);
                              }}
                              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                          >
                              <FaMoneyBillWave className="text-green-600" /> <span>Add Payment</span>
                          </button>

                          {/* 1. View Details */}
                          <button
                            onClick={() => {
                              handleView(p);
                              setActiveDropdown(null);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                          >
                            <FaEye className="text-blue-500" /> View Details
                          </button>
                              {/* 2. Edit Purchase */}
                              <button
                                onClick={() => {
                                  handleEdit(p);
                                  setActiveDropdown(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                              >
                                <FiEdit className="text-green-600" /> Edit
                              </button>

                              {/* Status Toggle Button */}
                                  {(() => {
                                    const isDueCleared = (p.dueAmount || 0) <= 0;
                                    const isComplete = p.status === "Complete";

                                    return (
                                      <button
                                        onClick={() => {
                                          handleStatusChange(p);
                                          setActiveDropdown(null);
                                        }}
                                        disabled={!isComplete && !isDueCleared}
                                        className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${
                                          !isComplete && !isDueCleared
                                            ? "text-gray-400 cursor-not-allowed opacity-60"
                                            : "text-gray-700 hover:bg-gray-50 cursor-pointer"
                                        }`}
                                        title={
                                          !isComplete && !isDueCleared
                                            ? "Clear due amount to mark as Complete"
                                            : isComplete
                                            ? "Current Status: Complete (Click to revert)"
                                            : "Current Status: Pending (Click to complete)"
                                        }
                                      >
                                        <FaExchangeAlt
                                          className={
                                            !isComplete && !isDueCleared ? "text-gray-400" : "text-orange-500"
                                          }
                                        />
                                        {/* Label Logic */}
                                        {isComplete ? "Mark Pending" : "Mark Complete"}
                                      </button>
                                    );
                                  })()}

                              {/* 3. Delete Purchase */}
                              <button
                                onClick={() => {
                                  handleSingleDelete(p.id);
                                  setActiveDropdown(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 flex items-center gap-2"
                              >
                                <RiDeleteBin6Line className="text-red-500" /> Delete
                              </button>
                        </div>
                      </SmartDropdown>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="print-hidden">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalRecords={filteredPurchases.length}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
      <PurchaseMedicineAddPaymentModal
        isOpen={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        purchase={paymentPurchaseData}
        onPaymentSuccess={() => fetchPurchases()}
      />
    </div>
  );
};

export default PurchaseMedicine;
